import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './AdminContact.css';
import { FaSave, FaHeadset, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaUsers } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';

const AdminContact = () => {
    const [isSaving, setIsSaving] = useState(false);
    const [contactId, setContactId] = useState(null);
    const [contactData, setContactData] = useState({
        header: {
            title: "Let's Connect",
            subtitle: "Fill out the form below and our strategy team will reach out within 24 hours to schedule your session."
        },
        info: {
            email: "hello@nimaisolutions.com",
            phone: "+1 (555) 000-0000",
            address: "123 Tech District, Suite 400, San Francisco, CA 94103"
        },
        form: {
            title: "Get a Free Consultation",
            buttonText: "Book My Free Strategy Call",
            note: "No obligation. Free 30-minute strategy call with an expert."
        },
        trusted: {
            title: "Trusted by Global Innovators"
        }
    });

    useEffect(() => {
        fetchContactContent();
    }, []);

    const fetchContactContent = async () => {
        try {
            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/contact-pages', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')} `,
                    'Accept': 'application/json'
                }
            });
            let data = null;

            if (Array.isArray(result)) {
                data = result[0];
            } else if (result.data) {
                data = Array.isArray(result.data) ? result.data[0] : result.data;
            }

            if (ok && data) {
                setContactId(data.id);
                setContactData({
                    header: {
                        title: data.page_title,
                        subtitle: data.page_description
                    },
                    info: {
                        email: data.email_address,
                        phone: data.phone_number,
                        address: data.physical_address
                    },
                    form: {
                        title: data.form_title,
                        buttonText: data.submit_button_text,
                        note: data.form_footer_note
                    },
                    trusted: {
                        title: data.social_proof_title
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching contact content:', error);
            toast.error('Failed to load contact content');
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        const method = contactId ? 'PUT' : 'POST';
        const url = contactId
            ? `https://aayushrealproperties.in/nimaibackend/public/api/contact-pages/${contactId}`
            : 'https://aayushrealproperties.in/nimaibackend/public/api/contact-pages';

        const payload = {
            page_title: contactData.header.title,
            page_description: contactData.header.subtitle,
            email_address: contactData.info.email,
            phone_number: contactData.info.phone,
            physical_address: contactData.info.address,
            form_title: contactData.form.title,
            submit_button_text: contactData.form.buttonText,
            form_footer_note: contactData.form.note,
            social_proof_title: contactData.trusted.title
        };

        try {
            const { ok, data: result } = await safeFetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (ok) {
                if (!contactId) setContactId(result.id);
                toast.success('Contact page content updated successfully!');
            } else {
                toast.error(result.message || 'Failed to update contact content');
            }
        } catch (error) {
            console.error('Error saving contact content:', error);
            toast.error('Network error while saving');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Manage <span>Contact Page</span></h1>
                        <p>Customize how clients reach out to your team.</p>
                    </div>
                    <button className="save-btn" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Updating...' : <><FaSave /> Save Changes</>}
                    </button>
                </header>

                <div className="admin-contact-grid">
                    {/* Hero & Header Section */}
                    <div className="admin-card-section">
                        <div className="section-header">
                            <h3><FaHeadset /> Hero & Header</h3>
                        </div>
                        <div className="admin-form-group">
                            <label>Page Title</label>
                            <input
                                type="text"
                                value={contactData.header.title}
                                onChange={(e) => setContactData({ ...contactData, header: { ...contactData.header, title: e.target.value } })}
                            />
                        </div>
                        <div className="admin-form-group">
                            <label>Subtitle / Description</label>
                            <textarea
                                rows="3"
                                value={contactData.header.subtitle}
                                onChange={(e) => setContactData({ ...contactData, header: { ...contactData.header, subtitle: e.target.value } })}
                            ></textarea>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="admin-card-section">
                        <div className="section-header">
                            <h3><FaEnvelope /> Contact Information</h3>
                        </div>
                        <div className="admin-form-group">
                            <label><FaEnvelope /> Email Address</label>
                            <input
                                type="email"
                                value={contactData.info.email}
                                onChange={(e) => setContactData({ ...contactData, info: { ...contactData.info, email: e.target.value } })}
                            />
                        </div>
                        <div className="admin-form-group">
                            <label><FaPhoneAlt /> Phone Number</label>
                            <input
                                type="text"
                                value={contactData.info.phone}
                                onChange={(e) => setContactData({ ...contactData, info: { ...contactData.info, phone: e.target.value } })}
                            />
                        </div>
                        <div className="admin-form-group">
                            <label><FaMapMarkerAlt /> Physical Address</label>
                            <textarea
                                rows="2"
                                value={contactData.info.address}
                                onChange={(e) => setContactData({ ...contactData, info: { ...contactData.info, address: e.target.value } })}
                            ></textarea>
                        </div>
                    </div>

                    {/* Form Customization */}
                    <div className="admin-card-section">
                        <div className="section-header">
                            <h3><FaRegFileAlt /> Consultation Form</h3>
                        </div>
                        <div className="admin-form-group">
                            <label>Form Title</label>
                            <input
                                type="text"
                                value={contactData.form.title}
                                onChange={(e) => setContactData({ ...contactData, form: { ...contactData.form, title: e.target.value } })}
                            />
                        </div>
                        <div className="admin-form-group">
                            <label>Submit Button Text</label>
                            <input
                                type="text"
                                value={contactData.form.buttonText}
                                onChange={(e) => setContactData({ ...contactData, form: { ...contactData.form, buttonText: e.target.value } })}
                            />
                        </div>
                        <div className="admin-form-group">
                            <label>Form Footer Note</label>
                            <input
                                type="text"
                                value={contactData.form.note}
                                onChange={(e) => setContactData({ ...contactData, form: { ...contactData.form, note: e.target.value } })}
                            />
                        </div>
                    </div>

                    {/* Trusted Section */}
                    <div className="admin-card-section">
                        <div className="section-header">
                            <h3><FaUsers /> Social Proof</h3>
                        </div>
                        <div className="admin-form-group">
                            <label>Trusted Section Title</label>
                            <input
                                type="text"
                                value={contactData.trusted.title}
                                onChange={(e) => setContactData({ ...contactData, trusted: { ...contactData.trusted, title: e.target.value } })}
                            />
                        </div>
                        <p className="admin-hint">Client logos are currently managed via the asset folder.</p>
                    </div>
                </div>
            </main>
        </div>
    );
};

// Simple icon for internal use
const FaRegFileAlt = () => <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 384 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm64 236c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12v8zm0-64c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12v8zm0-64c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12v8zM384 121.9v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z"></path></svg>;

export default AdminContact;
